import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, TrendingUp, TrendingDown, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { createPageUrl } from '@/utils';
import { formatSignalTypeLabel } from '@/components/signals/SignalsFeedCard';
import { formatDeadline } from '@/lib/homeDealTasks';
import PilotTile, { TileTabs, TileEmpty, TileSkeleton } from './PilotTile';
import { markOnce } from './pilotPerf';

const LIMIT = 5;
// Same 12-week window Home already uses for account momentum (twelveWeeksAgo).
const WINDOW_DAYS = 84;

/** Same two lanes as pages/Signals.jsx displayMomentumScore. */
function signalScore(row) {
  const raw = row?.momentum_score_change;
  if (raw != null && raw !== '') {
    const n = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(/–/g, '-'));
    if (!Number.isNaN(n)) return n;
  }
  if (row?.signal_score != null && !Number.isNaN(Number(row.signal_score))) return Number(row.signal_score);
  return null;
}

function sourceUrl(row) {
  const u = row?.source_url || row?.full_url || row?.news_source_url || '';
  return /^https?:\/\//i.test(u) ? u : null;
}

function signalDate(row) {
  const d = new Date(row?.date_published || row?.weekly_period || row?.created_at || '');
  return Number.isNaN(d.getTime()) ? null : d;
}

export default function TopSignalsTile({ momentumScores, accounts, loading }) {
  const [tab, setTab] = useState('fav');

  const { favourable, unfavourable } = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - WINDOW_DAYS);
    const rows = (momentumScores || [])
      .map((row) => ({ row, score: signalScore(row), date: signalDate(row) }))
      .filter((s) => s.score != null && s.score !== 0 && s.date && s.date >= cutoff && (s.row.summary || s.row.reason));
    return {
      favourable: rows.filter((s) => s.score > 0).sort((a, b) => b.score - a.score),
      unfavourable: rows.filter((s) => s.score < 0).sort((a, b) => a.score - b.score),
    };
  }, [momentumScores]);

  if (momentumScores?.length) markOnce('signals_data');
  const list = tab === 'fav' ? favourable : unfavourable;

  const accountHref = (row) => {
    const acc = (accounts || []).find((a) => a.account_id === row.account_id || a.account_name === row.institution_name);
    return acc?.id ? createPageUrl(`AccountFinal?id=${acc.id}`) : null;
  };

  return (
    <PilotTile
      testId="pilot-tile-signals"
      icon={Zap}
      title="Top Signals"
      href={createPageUrl('Signals')}
      right={(
        <TileTabs
          label="Signal view"
          value={tab}
          onChange={setTab}
          tabs={[
            { value: 'fav', label: `Favourable (${favourable.length})` },
            { value: 'unfav', label: `Unfavourable (${unfavourable.length})` },
          ]}
        />
      )}
    >
      {loading ? (
        <TileSkeleton />
      ) : list.length === 0 ? (
        <TileEmpty>{tab === 'fav' ? 'No favourable signals in the last 12 weeks.' : 'No unfavourable signals in the last 12 weeks.'}</TileEmpty>
      ) : (
        list.slice(0, LIMIT).map(({ row, score, date }) => {
          const up = score > 0;
          const href = accountHref(row);
          const typeLabel = row.signal_type ? formatSignalTypeLabel(row.signal_type) : null;
          return (
            <div key={row.id} className="flex items-start gap-3 rounded-lg p-2 hover:bg-gray-50">
              <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${up ? 'bg-green-100' : 'bg-red-100'}`}>
                {up ? <TrendingUp className="w-4 h-4 text-green-600" aria-hidden="true" /> : <TrendingDown className="w-4 h-4 text-red-600" aria-hidden="true" />}
              </div>
              <div className="min-w-0 flex-1">
                {/* Same as the Signals page (SignalsFeedCard): the summary links to its source. */}
                {sourceUrl(row) ? (
                  <a
                    href={sourceUrl(row)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group/src flex items-start gap-1 text-sm font-medium leading-snug text-[#0A1A2F] underline decoration-[#00C2C2] underline-offset-2 hover:text-[#00C2C2]"
                  >
                    <span className="line-clamp-2">{row.summary || row.reason}</span>
                    <ExternalLink className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#00C2C2]" aria-hidden="true" />
                  </a>
                ) : (
                  <p className="line-clamp-2 text-sm font-medium leading-snug text-[#0A1A2F]">{row.summary || row.reason}</p>
                )}
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-[#5C6B7A]">
                  {href ? (
                    <Link to={href} className="truncate hover:text-[#00C2C2]">{row.institution_name || '—'}</Link>
                  ) : (
                    <span className="truncate">{row.institution_name || '—'}</span>
                  )}
                  <span aria-hidden="true">·</span>
                  <span>{formatDeadline(date)}</span>
                  {typeLabel ? <Badge variant="outline" className="text-[10px] h-5 px-1.5">{typeLabel}</Badge> : null}
                </div>
              </div>
              <div className={`flex-shrink-0 text-lg font-bold ${up ? 'text-green-600' : 'text-red-600'}`}>
                {up ? '+' : ''}{Number.isInteger(score) ? score : score.toFixed(1)}
              </div>
            </div>
          );
        })
      )}
    </PilotTile>
  );
}
